import { useTranslation } from 'react-i18next';
import { companyContactParts, FiscalBlock, ZatcaBlock, PricesIncludeTaxNote, SellerIdsBlock } from './blocks';
import type { PosSettings, ReceiptData, ReceiptTemplateSettings } from '@/types';
import { resolveFileUrl } from '@/utils/fileUrl';
import { formatMoney } from '@/utils/currency';
import ReceiptPromoPrice from '@/components/pos/ReceiptPromoPrice';
import { receiptPromoTotals, showReceiptDiscountPrint } from '@/utils/receiptPromo';

export interface LayoutProps {
  inv: ReceiptData;
  displayCompany: string;
  servedBy?: string;
  tmplSettings: ReceiptTemplateSettings;
  posSettings?: PosSettings | null;
  taxLabel: string;
}

/** Ultra-short thermal / paper-saver layout. */
export function ShortReceiptLayout({
  inv,
  displayCompany,
  servedBy,
  tmplSettings,
  posSettings,
  taxLabel,
}: LayoutProps) {
  const { t } = useTranslation();
  const shortInv = inv.name?.substring(Math.max(0, (inv.name?.length || 0) - 10)) || '';
  const contact = companyContactParts(inv, tmplSettings).join(' · ');
  const showPromoPrint = showReceiptDiscountPrint(posSettings);
  const promoTotals = receiptPromoTotals(inv);
  const showPromoRow = showPromoPrint && promoTotals.promo > 0;

  return (
    <div className="invoice layout-compact" style={{ maxWidth: 320, margin: '0 auto', padding: '10px 12px' }}>
      <div className="text-center mb-1">
        {tmplSettings.show_logo && inv.company_logo ? (
          <img src={resolveFileUrl(inv.company_logo)} alt="" className="mx-auto mb-1" style={{ maxWidth: tmplSettings.logo_width || 80, maxHeight: 36 }} />
        ) : null}
        <div className="font-bold text-sm uppercase leading-tight">{displayCompany}</div>
        <SellerIdsBlock posSettings={posSettings} align="center" className="text-[10px] text-gray-600 mt-0.5" />
        {contact ? <div className="text-[10px] text-gray-400 mt-0.5">{contact}</div> : null}
      </div>
      <div className="border-t border-dashed border-gray-300 my-1.5" />
      <div className="font-mono text-[10px] text-gray-700 space-y-0.5">
        <div className="flex justify-between"><span>#{shortInv}</span><span>{new Date(inv.creation || inv.invoice_date || Date.now()).toLocaleDateString('en-GB')}</span></div>
        {tmplSettings.show_customer ? <div>{inv.customer_name || t('receipt.walkIn')}</div> : null}
        {tmplSettings.show_served_by && servedBy ? <div className="text-gray-400">{t('receipt.by')} {servedBy}</div> : null}
      </div>
      <div className="border-t border-dashed border-gray-300 my-1.5" />
      <div className="space-y-1">
        {(inv.items || []).map((item: any, idx: number) => (
          <div key={idx} className="flex justify-between gap-2 text-[11px]">
            <span className="text-gray-800 leading-tight flex-1">
              {item.quantity}× {item.item_name}{item.variant_name ? ` (${item.variant_name})` : ''}
            </span>
            {tmplSettings.show_item_price !== false && (
              <span className="font-semibold whitespace-nowrap">
                <ReceiptPromoPrice item={item} show={showPromoPrint} field="amount" />
              </span>
            )}
          </div>
        ))}
      </div>
      <div className="border-t border-dashed border-gray-300 my-1.5" />
      {tmplSettings.show_totals !== false && (
        <div className="font-mono text-[11px] space-y-0.5">
          {showPromoRow ? (
            <div className="flex justify-between text-gray-500">
              <span>{t('receipt.promoShort')}</span>
              <span>− {formatMoney(promoTotals.promo)}</span>
            </div>
          ) : null}
          {(!posSettings || posSettings.show_discount_in_invoice_print !== false)
            && ((inv.discount_amount || 0) + (inv.write_off_amount || 0)) > 0 ? (
            <div className="flex justify-between text-gray-500">
              <span>{t('receipt.discShort')}</span>
              <span>− {formatMoney((inv.discount_amount || 0) + (inv.write_off_amount || 0))}</span>
            </div>
          ) : null}
          {(!posSettings || posSettings.show_tax_in_invoice !== false) && (inv.tax_amount || 0) > 0 ? (
            <div className="flex justify-between text-gray-500">
              <span>{taxLabel}</span>
              <span>{formatMoney(inv.tax_amount)}</span>
            </div>
          ) : null}
          <div className="flex justify-between font-bold text-sm pt-0.5">
            <span>{t('receipt.total')}</span>
            <span>{formatMoney(inv.net_total)}</span>
          </div>
          {inv?.customer_name && inv?.customer_name !== 'Walk In' && (inv?.previous_outstanding || 0) > 0 && (
            <>
              <div className="flex justify-between text-[11px] text-gray-500 mt-0.5">
                <span>Previous Outstanding:</span>
                <span>{formatMoney(inv.previous_outstanding)}</span>
              </div>
              <div className="flex justify-between font-bold text-[11px] text-gray-800">
                <span>Total Outstanding:</span>
                <span>{formatMoney((inv.previous_outstanding || 0) + (inv.balance_amount || 0))}</span>
              </div>
            </>
          )}
          <div className="flex justify-between text-gray-500">
            <span>{t('receipt.paid')}</span>
            <span>{formatMoney(inv.paid_amount)}</span>
          </div>
          <PricesIncludeTaxNote posSettings={posSettings} />
        </div>
      )}
      <FiscalBlock inv={inv} posSettings={posSettings} compact />
      <ZatcaBlock inv={inv} posSettings={posSettings} compact />
      {tmplSettings.footer_text ? (
        <div className="text-center text-[10px] text-gray-400 mt-1.5 italic">{tmplSettings.footer_text}</div>
      ) : null}
    </div>
  );
}

/** Modern digital card — optimized for PNG / WhatsApp. */
export function ModernReceiptLayout({
  inv,
  displayCompany,
  servedBy,
  tmplSettings,
  posSettings,
  taxLabel,
}: LayoutProps) {
  const { t } = useTranslation();
  const contact = companyContactParts(inv, tmplSettings);
  const showPromoPrint = showReceiptDiscountPrint(posSettings);
  const promoTotals = receiptPromoTotals(inv);
  const showPromoRow = showPromoPrint && promoTotals.promo > 0;
  const subtotalDisplay = showPromoRow ? promoTotals.original : promoTotals.charged;

  return (
    <div
      className="invoice layout-modern"
      style={{
        maxWidth: 420,
        margin: '0 auto',
        background: '#fff',
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: '0 8px 30px rgba(15,23,42,0.12)',
        fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif',
      }}
    >
      <div style={{ background: 'linear-gradient(135deg,#0f172a 0%,#1e3a5f 100%)', color: '#fff', padding: '20px 22px 18px' }}>
        <div className="flex items-start justify-between gap-3">
          <div>
            {tmplSettings.show_logo && inv.company_logo ? (
              <img
                src={resolveFileUrl(inv.company_logo)}
                alt=""
                className="mb-2 rounded bg-white/10 object-contain"
                style={{ maxWidth: tmplSettings.logo_width || 100, maxHeight: 48 }}
              />
            ) : null}
            <div className="text-lg font-bold tracking-tight">{displayCompany}</div>
            <SellerIdsBlock posSettings={posSettings} className="text-[11px] text-white/80 space-y-0.5 mt-1" />
          </div>
          <div className="text-end text-[11px] text-white/70">
            <div className="font-mono text-white font-semibold">{inv.name}</div>
            <div>{new Date(inv.creation || inv.invoice_date || Date.now()).toLocaleString()}</div>
          </div>
        </div>
        {tmplSettings.header_text ? (
          <p className="text-xs text-white/70 mt-2 whitespace-pre-wrap">{tmplSettings.header_text}</p>
        ) : null}
      </div>

      <div className="px-5 py-4 space-y-3">
        <div className="flex justify-between text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-400">{t('receipt.customer')}</div>
            <div className="font-semibold text-gray-900">
              {tmplSettings.show_customer ? (inv.customer_name || t('receipt.walkIn')) : '—'}
            </div>
          </div>
          {tmplSettings.show_served_by && servedBy ? (
            <div className="text-end">
              <div className="text-[10px] uppercase tracking-wider text-gray-400">{t('receipt.servedBy')}</div>
              <div className="font-medium text-gray-700">{servedBy}</div>
            </div>
          ) : null}
        </div>

        {contact.length ? (
          <div className="text-xs text-gray-500">{contact.join(' · ')}</div>
        ) : null}

        <div className="rounded-xl bg-slate-50 border border-slate-100 overflow-hidden">
          {(inv.items || []).map((item: any, idx: number) => (
            <div
              key={idx}
              className="flex justify-between gap-3 px-3 py-2.5 border-b border-slate-100 last:border-0 text-sm"
            >
              <div className="min-w-0">
                <div className="font-medium text-gray-900 break-words">
                  {tmplSettings.show_item_code && item.item_code ? `${item.item_code} · ` : ''}
                  {item.item_name}
                  {item.variant_name ? ` — ${item.variant_name}` : ''}
                </div>
                <div className="text-xs text-gray-400">
                  {item.quantity} {tmplSettings.show_item_price !== false && <> × <ReceiptPromoPrice item={item} show={showPromoPrint} field="rate" /></>}
                </div>
              </div>
              {tmplSettings.show_item_price !== false && (
                <div className="font-semibold text-gray-900 whitespace-nowrap">
                  <ReceiptPromoPrice item={item} show={showPromoPrint} field="amount" />
                </div>
              )}
            </div>
          ))}
        </div>

        {tmplSettings.show_totals !== false && (
          <div className="space-y-1 text-sm">
            <div className="flex justify-between text-gray-500">
              <span>{t('receipt.subtotal')}</span>
              <span>{formatMoney(subtotalDisplay)}</span>
            </div>
            {showPromoRow ? (
              <div className="flex justify-between text-emerald-700">
                <span>{t('receipt.promotions')}</span>
                <span>− {formatMoney(promoTotals.promo)}</span>
              </div>
            ) : null}
            {(!posSettings || posSettings.show_discount_in_invoice_print !== false)
              && ((inv.discount_amount || 0) + (inv.write_off_amount || 0)) > 0 ? (
              <div className="flex justify-between text-gray-500">
                <span>{t('receipt.discount')}</span>
                <span>− {formatMoney((inv.discount_amount || 0) + (inv.write_off_amount || 0))}</span>
              </div>
            ) : null}
            {(!posSettings || posSettings.show_tax_in_invoice !== false) && (inv.tax_amount || 0) > 0 ? (
              <div className="flex justify-between text-gray-500">
                <span>{taxLabel}</span>
                <span>{formatMoney(inv.tax_amount)}</span>
              </div>
            ) : null}
            <div className="flex justify-between text-base font-bold text-gray-900 pt-2 border-t border-slate-100">
              <span>{t('receipt.total')}</span>
              <span>{formatMoney(inv.net_total)}</span>
            </div>
            {inv?.customer_name && inv?.customer_name !== 'Walk In' && (inv?.previous_outstanding || 0) > 0 && (
              <>
                <div className="flex justify-between text-sm text-gray-500 mt-1">
                  <span>Previous Outstanding:</span>
                  <span>{formatMoney(inv.previous_outstanding)}</span>
                </div>
                <div className="flex justify-between font-bold text-sm text-gray-800">
                  <span>Total Outstanding:</span>
                  <span>{formatMoney((inv.previous_outstanding || 0) + (inv.balance_amount || 0))}</span>
                </div>
              </>
            )}
            <div className="flex justify-between text-emerald-700 font-medium">
              <span>{t('receipt.paid')}</span>
              <span>{formatMoney(inv.paid_amount)}</span>
            </div>
            {(inv.balance_amount || 0) > 0 ? (
              <div className="flex justify-between text-amber-700">
                <span>{t('receipt.balanceDue')}</span>
                <span>{formatMoney(inv.balance_amount)}</span>
              </div>
            ) : (
              <div className="text-center text-xs font-semibold text-emerald-600 bg-emerald-50 rounded-lg py-1.5 mt-1">{t('receipt.paidInFull')}</div>
            )}
            <PricesIncludeTaxNote posSettings={posSettings} />
          </div>
        )}

        <FiscalBlock inv={inv} posSettings={posSettings} />
        <ZatcaBlock inv={inv} posSettings={posSettings} />

        {tmplSettings.footer_text ? (
          <p className="text-center text-xs text-gray-400 whitespace-pre-wrap pt-1">{tmplSettings.footer_text}</p>
        ) : null}
      </div>
    </div>
  );
}
